import { IManageShop, IManageShopAction } from 'models/shop/manageshop';
import { IShopProfile, IManageShopSubscriptionPlan } from 'models/shop/shopprofile';
import { IStore } from 'redux/IStore';
import * as i18n from 'i18next';
import { IShopShippingOption } from 'models/shop/shopshippingoption';
import { actions as notifActions } from 'redux-notifications';
import { reset } from 'redux-form';
const { notifSend } = notifActions;

/** Domain Values actions */
import { getCountryDomainValues } from 'modules/domainvalues';
import {
	addAdmin,
	addMember,
	addMemberPermission,
	addOffice,
	editMember,
	getActiveShopSubscriptionPlan,
	getMembersDifferences,
	getOfficesDifferences,
	getOfficeTimezone,
	getShopAdmins,
	getShopOffices,
	isAdmin,
	removeAdmin,
	removeMember,
	removeMemberPermission,
	removeOffice,
	removePermission,
	sequential,
	setDefaultOffice,
	updateOffice,
} from '../helpers';
import { FORM_NAME_MANAGE_SHOP } from 'models/constants';
import { fetchUserCardById } from 'redux/modules/user/usercard';
import { INodeDto } from 'models/common';
import { IUserCard } from 'models/user/usercard';
import { requestHelper } from 'redux/helpers';
import { idFromEntityId, setOtherZone } from 'helpers/common';
import { IRule } from 'models/shop/members';
import { IOffice } from 'models/shop/office';
import { IanaToWindowsTimezone } from '../timezones/IanaToWindowsTimezone';

/** Action Types */
export const REQUEST_START: string = 'manageshop/REQUEST_START';
export const REQUEST_FAILURE: string = 'manageshop/REQUEST_FAILURE';
export const GET_SHOP_SUCCESS: string = 'manageshop/GET_SHOP_SUCCESS';
export const PUT_SHOP_SUCCESS: string = 'manageshop/PUT_SHOP_SUCCESS';
export const PUT_OFFICE_SUCCESS: string = 'manageshop/PUT_OFFICE_SUCCESS';
export const GET_COUNTRIES_SUCCESS: string = 'manageshop/GET_COUNTRIES_SUCCESS';
export const GET_SHIPPING_OPTIONS_SUCCESS: string = 'manageshop/GET_SHIPPING_OPTIONS_SUCCESS';
export const SET_IS_EDIT_MODE: string = 'manageshop/SET_IS_EDIT_MODE';
export const CLEAR_FORM_DATA: string = 'manageshop/CLEAR_FORM_DATA';

/** Initial State */
const initialState: IManageShop = {
	shop: null,
};

function commonError(res: any, dispatch: any) {
	const status: number = res.status;
	const statusText: string = res.statusText;
	if (!res) {
		console.error('Response missing for commonError nandling');
		return;
	}
	console.error(res);
	if (!res.json) {
		dispatch(requestFailure(res));
		return Promise.resolve(res);
	}
	return res
		.json()
		.then((res) => {
			res.status = status;
			res.statusText = statusText;
			dispatch(requestFailure(res));
		})
		.catch(() => {
			dispatch(requestFailure(res));
			return res;
		});
}

/** Reducer */
export function manageShopReducer(state = initialState, action: IManageShopAction): IManageShop {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case GET_SHOP_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				shop: {
					...action.payload.shop,
				},
			});

		case PUT_SHOP_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				shop: action.payload.shop,
			});

		case GET_SHIPPING_OPTIONS_SUCCESS:
			return Object.assign({}, state, {
				shop: {
					...state.shop,
					shippingOptions: action.payload.shippingOptions,
				},
				isFetching: false,
			});

		case SET_IS_EDIT_MODE:
			return Object.assign({}, state, {
				isEditMode: action.payload.isEditMode,
			});

		case CLEAR_FORM_DATA:
			return Object.assign({}, state, {
				shop: null,
			});

		default:
			return state;
	}
}

export function clearFormData() {
	return (dispatch) => {
		dispatch(clearManageShopData());
		dispatch(reset(FORM_NAME_MANAGE_SHOP));
	};
}

export function resetFormData() {
	return (dispatch) => {
		dispatch(reset(FORM_NAME_MANAGE_SHOP));
	};
}

let validRequest: number;
let oldAbortController: AbortController | undefined;

/** Async Action Creator */
export function getShop(
	shopId: string,
	fetchShipping: boolean = false,
	fetchMembers: boolean = false,
	fetchShopPlan: boolean = false,
	shouldClearForm: boolean = true,
) {
	return (dispatch, getState: () => IStore): Promise<any> => {
		if (shopId) {
			dispatch(setIsEditMode(true));
			dispatch(requestStart());

			if (shouldClearForm) {
				dispatch(clearManageShopData());
			}

			const thisRequest = Math.random();
			validRequest = thisRequest;
			if (typeof AbortController !== 'undefined') {
				if (oldAbortController && !oldAbortController.signal.aborted) {
					oldAbortController.abort();
				}
				oldAbortController = new AbortController();
			}

			// GET shop
			return fetch(getState().fetchDefault.baseAPI + 'api/shopprofile/' + shopId, {
				...getState().fetchDefault.requestInit,
				signal: oldAbortController ? oldAbortController.signal : undefined,
			})
				.then((res) => {
					if (validRequest !== thisRequest) {
						return;
					}
					if (res.ok) {
						return (
							res
								.json()
								// GET shop admins
								.then((res: IShopProfile) => {
									if (fetchMembers) {
										return getShopAdmins(getState, res.id, oldAbortController).then((admins) => ({
											...res,
											admins,
										}));
									}
									return res;
								})
								// GET shop offices
								.then((shop: IShopProfile) => {
									return getShopOffices(getState, shopId, fetchMembers, oldAbortController)
										.then((offices) => ({
											...shop,
											offices,
										}))
										.catch((res) => {
											return commonError(res, dispatch);
										});
								})
								// GET owner
								.then((shop: IShopProfile) => {
									return fetchUserCardById(getState, shop.ownerId)
										.then((ownerData) => ({
											...shop,
											ownerData,
										}))
										.catch((res) => {
											return commonError(res, dispatch);
										});
								})
								.then((shop: IShopProfile) => {
									if (fetchShopPlan) {
										return getActiveShopSubscriptionPlan(getState, shopId)
											.then((currentShopSubscriptionPlan: IManageShopSubscriptionPlan) => {
												return { ...shop, currentShopSubscriptionPlan };
											})
											.catch((res) => {
												// It goes into catch if no subscription plan or error
												console.error(res);
												return shop;
											});
									}
									return shop;
								})
								.then((shop: IShopProfile | void) => {
									if (fetchShipping) {
										dispatch(getShopShippingOptions(shopId));
										dispatch(getCountryDomainValues());
									}
									if (shop) {
										dispatch(getShopSuccess(shop));
									}
								})
						);
					} else {
						return commonError(res, dispatch);
					}
				})
				.catch((err) => {
					if ((oldAbortController && oldAbortController.signal.aborted) || validRequest !== thisRequest) {
						return;
					}
					dispatch(requestFailure(err));
				});
		} else {
			dispatch(setIsEditMode(false));
			dispatch(clearFormData());
			return Promise.resolve(false);
		}
	};
}

/** Action Creator */
export function getShopShippingOptions(shopId: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Shop/' + shopId + '/ShippingOptions',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res) => {
						dispatch(getShippingOptionsSuccess(res));
					});
				} else {
					return commonError(res, dispatch);
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

function addOfficeAdmin(getState: () => IStore, shop: IShopProfile, user: IUserCard | INodeDto) {
	return Promise.all(
		shop.offices.map((office) => {
			addMemberPermission(getState, office.id, user.entityId, 'Create,Read,Update,Delete');
		}),
	);
}

function removeOfficeAdmin(getState: () => IStore, shop: IShopProfile, user: IUserCard | INodeDto) {
	return Promise.all(
		shop.offices.map((office) => {
			requestHelper<IRule[]>(
				fetch(
					getState().fetchDefault.baseAPI +
						'api/permission/getByObject?type=Office&id=' +
						idFromEntityId(office.id, false),
					Object.assign({}, getState().fetchDefault.requestInit),
				),
			).then((rules) => {
				rules.map((rule) => {
					if (rule.sub === idFromEntityId(user.entityId, false)) {
						removePermission(getState, rule);
					}
				});
			});
		}),
	);
}

function removeOfficeAndOfficePermissions(getState: () => IStore, officeId: string) {
	const userRoles = getState().userPreferences.userRoles; // May need a page reload to get the newest user roles if you just added a new office and want to delete it
	const userId = getState().userPreferences.id;
	const isSiteAdmin = getState().userPreferences.isAdministrator;
	const isOfficeAdmin = isAdmin(officeId, userRoles);

	if (!!isSiteAdmin || isOfficeAdmin) {
		return requestHelper<IRule[]>(
			fetch(
				getState().fetchDefault.baseAPI +
					'api/permission/getByObject?type=Office&id=' +
					idFromEntityId(officeId, false),
				Object.assign({}, getState().fetchDefault.requestInit),
			),
		).then((rules) => {
			// Delete self last
			const ruleIndex = rules.map((rule) => rule.sub).indexOf(userId);
			rules.push(rules.splice(ruleIndex, 1)[0]); // if deleting as office admin put him at last place
			// Delete permissions first and then the office
			sequential(
				rules.map((rule) => () => {
					return removePermission(getState, rule);
				}),
			).then(() => {
				removeOffice(getState, officeId);
			});
		});
	} else {
		return null;
	}
}

/** Action Creator */
export function putShop(
	shop: IShopProfile,
	section?: 'offices' | 'members' | 'general',
	shouldClearForm: boolean = true,
) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		if (shop.socialLinksObj) {
			shop.socialLinks = JSON.stringify(shop.socialLinksObj);
		}

		let request: Promise<Partial<Response>> = Promise.resolve({ ok: true });

		const shopOwnerId = shop.ownerId;
		const shopAdmins = shop?.admins;
		const shopStaff = shop.offices
			.map((office) => office.staff)
			.reduce((acc, curVal) => {
				return acc.concat(curVal);
			}, []);

		if (section === 'offices') {
			const existing = getState().manageShop.shop.offices || [];
			const changed = shop.offices || [];
			const differences = getOfficesDifferences(existing, changed, shop.id);
			if (differences.length) {
				request = request
					.then(() =>
						Promise.all(
							differences.map((todo): Promise<any> => {
								switch (todo.action) {
									case 'create':
										return getOfficeTimezone(todo.item).then((res) => {
											const zoneInfo = IanaToWindowsTimezone[res.timeZoneId]; // Convert IANA timezone to Windows timezone
											const officeWithTimezone: IOffice = { ...todo.item, zoneInfo: zoneInfo };
											// convert working time to office timezone
											if (officeWithTimezone.officeTimes) {
												officeWithTimezone.officeTimes.map((officeTime) => {
													var newOpenTime = setOtherZone(new Date(officeTime.openTime), res.timeZoneId);
													var newCloseTime = setOtherZone(new Date(officeTime.closeTime), res.timeZoneId);
													officeTime.openTime = newOpenTime;
													officeTime.closeTime = newCloseTime;
												});
											}
											addOffice(getState, officeWithTimezone);
										});
									case 'delete':
										return removeOfficeAndOfficePermissions(getState, todo.item.id);
									case 'update':
										return updateOffice(getState, todo.item);
									default:
										console.error('Unsupported action');
								}
							}),
						),
					)
					.then(() => ({ ok: true }));
			}
			request = request.then(() => setDefaultOffice(getState, shop).then(() => ({ ok: true })));
		} else if (section === 'members') {
			let memberRequests: Array<Promise<any>> = [];
			// ADMINS
			const differences = getMembersDifferences(getState().manageShop.shop, shop);
			if (differences.length) {
				memberRequests = [
					...memberRequests,
					...differences.map((todo) => {
						const userId = JSON.parse(todo.item.entityId).id;
						const findMemberAll = shopStaff.filter((member) => member.userId === userId) || []; // Same member from all offices (remove member permissions when adding member)
						const findOffices =
							shopStaff.filter((member) => member.userId === userId).map((member) => member.officeId) || []; // Offices of the specific member (add member permissions back if removing admin)

						if (shopOwnerId !== userId) {
							switch (todo.action) {
								// Remove all member permissions from current member > add shop admin permissions > add office admin permissions
								case 'create': {
									// Add the member object to array if the office member has member permissions
									const arrayWithReadRulesMembers = [];
									if (findMemberAll) {
										findMemberAll.map((member: any) => {
											if (member.rules) {
												member.rules.map((rule) => {
													if (rule.actions === 'Read') {
														arrayWithReadRulesMembers.push(member);
													}
												});
											}
										});
									}
									return (
										sequential(
											arrayWithReadRulesMembers.map((member) => () => removeMemberPermission(getState, member)),
										) &&
										addAdmin(getState, shop.id, todo.item) &&
										addOfficeAdmin(getState, shop, todo.item)
									);
								}
								// Remove shop admin permissions > remove office admin permissions > add member permissions to the current member based on his offices
								case 'delete':
									return (
										removeAdmin(getState, todo.item) &&
										removeOfficeAdmin(getState, shop, todo.item) &&
										sequential(
											findOffices.map(
												(officeId) => () => addMemberPermission(getState, officeId, todo.item.id, 'Read'),
											),
										)
									); // we need to remove admin from all offices seperately
								default:
									console.error('Unsupported action');
							}
						}
					}),
				];
			}

			// OFFICES MEMBERS
			const offices = shop.offices || [];
			if (offices.length) {
				const existing = getState().manageShop.shop.offices || [];
				memberRequests = [
					...memberRequests,
					...offices.map((office): Promise<any> => {
						if (!office.id) {
							console.error('Office without ID');
							return Promise.resolve(false);
						}
						const previous = existing.find((e) => e.id === office.id);
						if (!previous) {
							console.error('Cannot find office with ID ' + office.id);
							return Promise.resolve(false);
						}
						const differences = getMembersDifferences(previous, office);
						if (!differences.length) {
							return Promise.resolve(true);
						}
						return Promise.all(
							differences.map((todo) => {
								const userId = todo.item.userId;
								const findAdmin = shopAdmins.find((admin) => admin.id === userId); // Find if the user already has admin permissions
								const isOwner: boolean = shopOwnerId === userId ? true : false;
								let shouldChangePermission = true;

								// If the user is an admin or a shop owner add them only to resources and skip adding member permissions
								if (!!findAdmin || isOwner) {
									shouldChangePermission = false;
								}

								switch (todo.action) {
									case 'create':
										return addMember(getState, todo.item, office.id, shouldChangePermission);
									case 'delete':
										return removeMember(getState, todo.item, shouldChangePermission);
									case 'update':
										return editMember(getState, todo.item);
									default:
										console.error('Unsupported action');
								}
							}),
						);
					}),
				];
			}

			if (memberRequests.length) {
				request = Promise.all(memberRequests).then(() => ({ ok: true }));
			}
		} else {
			request = fetch(
				getState().fetchDefault.baseAPI + 'api/shopprofile/' + shop.id,
				Object.assign({}, getState().fetchDefault.requestInit, {
					method: 'PUT',
					body: JSON.stringify(shop),
				}),
			);
		}

		return request
			.then((res) => {
				if (res?.ok) {
					dispatch(putShopSuccess(shop));
					dispatch(getShop(shop.id, false, section === 'members', section === 'general', shouldClearForm));
					dispatch(
						notifSend({ message: i18n.t('Notification_Success_Shop_Updated'), kind: 'success', dismissAfter: 3000 }),
					);
				} else {
					commonError(res, dispatch);
					return Promise.reject(true); // return true to signify an error
				}
			})
			.catch((err) => {
				console.error(err);
				dispatch(requestFailure(err));
				return Promise.resolve(true); // return true to signify an error
			});
	};
}

/** Action Creator */
export function requestStart(): IManageShopAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IManageShopAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function getShopSuccess(shop: IShopProfile): IManageShopAction {
	return {
		type: GET_SHOP_SUCCESS,
		payload: {
			shop,
		},
	};
}

/** Action Creator */
export function putShopSuccess(shop: IShopProfile): IManageShopAction {
	return {
		type: PUT_SHOP_SUCCESS,
		payload: {
			message: 'OK',
			shop,
		},
	};
}

/** Action Creator */
export function getShippingOptionsSuccess(shippingOptions: IShopShippingOption[]): IManageShopAction {
	return {
		type: GET_SHIPPING_OPTIONS_SUCCESS,
		payload: {
			shippingOptions,
		},
	};
}

/** Action Creator */
export function setIsEditMode(isEditMode: boolean): IManageShopAction {
	return {
		type: SET_IS_EDIT_MODE,
		payload: {
			isEditMode,
		},
	};
}

/** Action Creator */
export function clearManageShopData(): IManageShopAction {
	return {
		type: CLEAR_FORM_DATA,
	};
}
